'use client';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fadeIn } from '@/lib/animations';
import { Package, Apple, Snowflake, Thermometer, TriangleAlert as AlertTriangle } from 'lucide-react';

const storageTypes = [
  {
    title: 'General Storage',
    description: 'Over 250,000 sq ft of dry storage at our Milwaukee 6th Street facility with climate-controlled environments and flexible racking solutions.',
    icon: Package,
    color: 'bg-blue-500',
    delay: 0.1,
  },
  {
    title: 'Food Storage',
    description: 'FDA-compliant food grade storage at our Waukesha facility with 140,000 sq ft of dry storage, 35,000 sq ft refrigerated, and 40,000 sq ft freezer space.',
    icon: Apple,
    color: 'bg-green-500',
    delay: 0.2,
  },
  {
    title: 'Cold Storage Warehouse',
    description: 'Temperature-controlled refrigerated storage maintaining 32°F to 50°F at our Waukesha facility for fresh products and cold chain logistics.',
    icon: Thermometer,
    color: 'bg-cyan-500',
    delay: 0.3,
  },
  {
    title: 'Freezer Storage',
    description: 'Sub-zero freezer storage at -10°F and below with 40,000 sq ft capacity at our Waukesha facility for frozen goods with precise temperature monitoring.',
    icon: Snowflake,
    color: 'bg-indigo-500',
    delay: 0.4,
  },
  {
    title: 'Hazmat Storage',
    description: 'Southeastern Wisconsin\'s only fully compliant HazMat warehouse at our Bayview facility with foam sprinkler systems and underground recovery tanks.',
    icon: AlertTriangle,
    color: 'bg-red-500',
    delay: 0.5,
  },
];

export function GreenBayStorageTypes() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
    rootMargin: '100px 0px',
  });

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          ref={ref}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeIn('up')}
          className="text-center mb-16"
        >
          <h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">
            Storage Solutions
          </h2>
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Comprehensive Warehouse Solutions & Storage Options
          </h3>
          <p className="max-w-3xl mx-auto text-gray-700 text-lg leading-relaxed">
            Lindner Logistics offers specialized warehousing solutions and warehouse space designed to meet the unique requirements 
            of different industries and product types. Our 3PL warehouse and distribution services include cold storage warehouse and refrigerated warehouse options for complete cold chain logistics.
          </p>
        </motion.div>

        <div className="relative max-w-4xl mx-auto">
          {/* Connecting Line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-brand-200 via-brand-400 to-brand-600 transform -translate-x-1/2 hidden md:block" />
          
          {storageTypes.map((storage, index) => {
            const [itemRef, itemInView] = useInView({
              triggerOnce: true,
              threshold: 0.3,
              rootMargin: '50px 0px',
            });

            const isLeft = index % 2 === 0;

            return (
              <motion.div
                key={index}
                ref={itemRef}
                initial="hidden"
                animate={itemInView ? "show" : "hidden"}
                variants={fadeIn(isLeft ? 'right' : 'left', storage.delay)}
                className={`relative flex items-center mb-16 ${
                  isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                } flex-col md:gap-8`}
              >
                {/* Content Card */}
                <div className={`w-full md:w-5/12 ${isLeft ? 'md:text-right' : 'md:text-left'} text-center`}>
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={itemInView ? { scale: 1, opacity: 1 } : { scale: 0.8, opacity: 0 }}
                    transition={{ delay: storage.delay + 0.2, duration: 0.5 }}
                    className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group"
                  >
                    <div className={`w-16 h-16 ${storage.color} rounded-2xl flex items-center justify-center text-white mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <storage.icon size={32} />
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 mb-4">
                      {storage.title}
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      {storage.description}
                    </p>
                  </motion.div>
                </div>

                {/* Center Bubble */}
                <div className="absolute left-1/2 transform -translate-x-1/2 z-10 hidden md:block">
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={itemInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                    transition={{ 
                      delay: storage.delay,
                      type: "spring",
                      stiffness: 200,
                      damping: 15
                    }}
                    className={`w-20 h-20 ${storage.color} rounded-full flex items-center justify-center text-white shadow-lg`}
                  >
                    <storage.icon size={28} />
                    {/* Pulse Animation */}
                    <motion.div
                      animate={itemInView ? { scale: [1, 1.2, 1], opacity: [0.3, 0, 0.3] } : {}}
                      transition={{ 
                        delay: storage.delay + 0.5,
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                      className={`absolute inset-0 ${storage.color} rounded-full`}
                    />
                  </motion.div>
                </div>

                {/* Spacer for mobile */}
                <div className="w-full md:w-5/12 md:block hidden" />
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          ref={ref}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeIn('up', 0.6)}
          className="text-center mt-16"
        >
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 max-w-2xl mx-auto">
            <h4 className="text-2xl font-bold text-gray-900 mb-4">
              Need Multiple Warehouse Solutions?
            </h4>
            <p className="text-gray-700 mb-6">
              Our distribution warehouse facilities can accommodate multiple warehousing solutions within the same location, 
              providing flexibility and efficiency for your diverse supply chain solutions and warehouse space needs.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-brand-600 hover:bg-brand-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Discuss Your Storage Needs
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}